import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Eye } from "lucide-react";

const ArticleCard = ({ article, variant = "default" }) => {
  const formattedDate = article.published_at 
    ? format(new Date(article.published_at), "MMM dd, yyyy")
    : format(new Date(article.created_at), "MMM dd, yyyy");

  if (variant === "featured") {
    return (
      <Link 
        to={`/articles/${article.slug}`}
        className="group relative flex flex-col h-full overflow-hidden bg-card border border-border hover:border-primary/50 transition-all duration-300"
        data-testid={`article-card-featured-${article.article_id}`}
      >
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={article.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=800"}
            alt={article.title}
            className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-4 mb-3">
            <span className="font-mono text-xs uppercase tracking-widest text-primary">
              {article.category?.name || "Article"}
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {formattedDate}
            </span>
          </div>
          <h3 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight leading-tight group-hover:text-primary transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-muted-foreground line-clamp-2 text-sm">
            {article.excerpt}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "hero") {
    return (
      <Link 
        to={`/articles/${article.slug}`}
        className="group relative flex flex-col h-full min-h-[400px] md:min-h-[500px] overflow-hidden bg-card border border-border hover:border-primary/50 transition-all duration-300"
        data-testid={`article-card-hero-${article.article_id}`}
      >
        <img
          src={article.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=1200"}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="relative mt-auto p-6 md:p-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="font-mono text-xs uppercase tracking-widest text-primary bg-primary/10 px-3 py-1">
              Featured
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {formattedDate}
            </span>
          </div>
          <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tighter leading-none group-hover:text-primary transition-colors">
            {article.title}
          </h2>
          <p className="mt-4 text-muted-foreground line-clamp-3 max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Eye className="h-4 w-4" />
              <span className="font-mono text-xs">{article.views?.toLocaleString() || 0} views</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Default list variant
  return (
    <Link 
      to={`/articles/${article.slug}`}
      className="group flex flex-col gap-4 pb-8 border-b border-border hover:border-primary transition-colors"
      data-testid={`article-card-${article.article_id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={article.featured_image || "https://images.unsplash.com/photo-1692663576031-ff42f3b4652d?w=600"}
          alt={article.title}
          className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div>
        <div className="flex items-center gap-4 mb-2">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            {article.category?.name || "Article"}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {formattedDate}
          </span>
          {(article.is_archive || article.category_id === "cat_tumblr_archive") && (
            <span className="font-mono text-xs text-primary/70 border border-primary/30 px-1.5 py-0.5 leading-none">Archive</span>
          )}
        </div>
        <h3 className="font-heading font-bold text-xl uppercase tracking-tight leading-tight group-hover:text-primary transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-muted-foreground line-clamp-2 text-sm">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 mt-3 text-muted-foreground">
          <Eye className="h-3 w-3" />
          <span className="font-mono text-xs">{article.views?.toLocaleString() || 0}</span>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
